import React from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import {
    openEntityViewSameTab,
    openEntityViewNewTab,
    copyEntityViewLinkWithToast,
} from '../../utils/entityViewNavigationUtils';

export interface ReferenceFieldViewIconProps {
    entityType: string;
    entityId: string;
    /** e.g. "Set", "Workflow" — used in tooltips. */
    entityLabel?: string;
    disabled?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

/**
 * View (eye) control: left-click navigate, middle-click new tab, right-click copy link.
 */
export const ReferenceFieldViewIcon: React.FC<ReferenceFieldViewIconProps> = ({
    entityType,
    entityId,
    entityLabel,
    disabled = false,
    size = 'sm',
}) => {
    const id = (entityId ?? '').trim();
    if (!id) return null;

    const label = entityLabel ?? entityType;
    const idTooltip = `ID: ${id}`;
    const actionTooltip = `${idTooltip}. Middle-click: new tab. Right-click: copy link.`;

    const handleViewMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 1 || disabled) return;
        e.preventDefault();
        e.stopPropagation();
        openEntityViewNewTab(entityType, id);
    };

    const handleViewClick = (e: React.MouseEvent) => {
        if (e.button !== 0 || disabled) return;
        e.preventDefault();
        e.stopPropagation();
        openEntityViewSameTab(entityType, id);
    };

    const handleViewContextMenu = (e: React.MouseEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        void copyEntityViewLinkWithToast(entityType, id);
    };

    return (
        <Tooltip label={actionTooltip}>
            <ActionIcon
                variant="subtle"
                size={size}
                onClick={handleViewClick}
                onMouseDown={handleViewMouseDown}
                onContextMenu={handleViewContextMenu}
                onAuxClick={(e) => {
                    if (e.button !== 1 || disabled) return;
                    e.preventDefault();
                    e.stopPropagation();
                    openEntityViewNewTab(entityType, id);
                }}
                disabled={disabled}
                aria-label={`View ${label}`}
            >
                <IconEye size={16} />
            </ActionIcon>
        </Tooltip>
    );
};
